import { Link, type LinkProps } from "@tanstack/react-router";
import { type VariantProps, cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import React, { useCallback, useMemo, useRef } from "react";

import { cn } from "~/lib/cn";
import { trackCampaignClick } from "~/lib/monitoring/track-event";
import type {
  TrackingCampaign,
  TrackingMedium,
} from "~/lib/monitoring/tracking-parameters";
import { resolveLinkAttrs } from "~/lib/shared/link-rel";

export const buttonVariants = cva(
  "inline-flex cursor-pointer items-center [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 justify-center rounded-lg text-sm font-medium transition duration-200 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-white border border-border text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground",
        primary:
          "bg-gradient-to-b from-purple-700 to-purple-800 border border-purple-800 hover:border-purple-700 text-primary-foreground hover:from-purple-600 hover:to-purple-700 active:from-purple-800 active:to-purple-900 hover:shadow",
        fancy:
          "dcm-shiny bg-purple-700 hover:bg-purple-600 active:bg-purple-800 text-primary-foreground duration-800 ease-out-expo active:duration-150 rotate-dynamic hover:shadow-md border border-purple-800 hover:border-purple-700",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        ghost: "hover:bg-purple-50 hover:text-purple-900",
        "destructive-ghost": "text-red-600 hover:bg-red-50",
        link: "text-text-primary underline-offset-4 hover:underline active:scale-100",
        pro: "text-white bg-gradient-pro-orange shadow shadow-orange-600/30 inset-ring inset-ring-orange-700/30 text-orange-50 flex items-center justify-center",
      },
      size: {
        default: "px-4 py-2 h-10",
        sm: "h-9 px-3",
        lg: "px-8 py-4 text-lg border-2 rounded-full",
        xl: "px-10 py-4 border-2 text-2xl rounded-full",
        icon: "h-10 w-10 text-lg rounded-full",
        "icon-sm": "h-8 w-8 text-sm rounded-full",
        "icon-nav": "h-12 w-12 text-lg rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    loading?: boolean;
    track?: {
      campaign: TrackingCampaign;
      medium: TrackingMedium;
    };
  };

/** The standard button: variants, sizes, a loading state and click tracking. */
export const Button = React.memo(
  React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        size,
        variant: look,
        onClick,
        track,
        loading,
        children,
        className,
        ...props
      },
      ref,
    ) => {
      const buttonRef = useRef<HTMLButtonElement>(null);
      const combinedRef = useMemo(() => {
        return (node: HTMLButtonElement | null) => {
          buttonRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        };
      }, [ref]);

      const trackClick = useCallback(
        (_e: React.MouseEvent<HTMLButtonElement>) => {
          if (window && track && buttonRef.current) {
            trackCampaignClick(track, buttonRef.current.innerText);
          }
        },
        [track],
      );

      const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
          if (look !== "fancy") {
            return;
          }
          const rotateAmount = 60;

          const button = buttonRef.current;
          if (!button) {
            return;
          }

          const rect = button.getBoundingClientRect();

          // Calculate relative mouse position within the button
          const xRatio = (e.clientX - rect.left) / rect.width;
          const yRatio = (e.clientY - rect.top) / rect.height;

          // Apply the shimmer effect
          button.style.setProperty("--e1x", `${30 + xRatio * 60}%`);
          button.style.setProperty("--e1y", `${5 + yRatio * 40}%`);
          button.style.setProperty("--e2x", `${35 + xRatio * 20}%`);
          button.style.setProperty("--e2y", `${35 + yRatio * 20}%`);

          // Calculate the button's center point
          const rotateX =
            -(e.clientY - rect.y - rect.height / 2) / rotateAmount;
          const rotateY = (e.clientX - rect.x - rect.width / 2) / rotateAmount;

          // Apply 3D rotation variables
          button.style.setProperty("--rotateX", `${rotateX}deg`);
          button.style.setProperty("--rotateY", `${rotateY}deg`);
        },
        [look],
      );

      const handleClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
          if (track) {
            trackClick(e);
          }
          if (onClick) {
            onClick(e);
          }
        },
        [track, trackClick, onClick],
      );

      const buttonClassName = useMemo(
        () => cn(buttonVariants({ size, variant: look }), className),
        [size, look, className],
      );

      return (
        <button
          ref={combinedRef}
          onMouseMove={handleMouseMove}
          {...props}
          onClick={handleClick}
          className={buttonClassName}
          disabled={loading || props.disabled}
        >
          {loading && <Loader2 className="mr-1 size-4 animate-spin" />}
          {children}
        </button>
      );
    },
  ),
);

Button.displayName = "Button";

export type ButtonLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> &
  VariantProps<typeof buttonVariants> & {
    track?: {
      campaign: TrackingCampaign;
      medium: TrackingMedium;
    };
    rel?: string;
    target?: React.HTMLAttributeAnchorTarget;
    href?: string;
    /** Mark an external destination as user-generated (coach-supplied). */
    ugc?: boolean;
  };

export const ButtonLink = ({
  size,
  variant: look,
  track,
  className,
  children,
  rel,
  target,
  href,
  ugc,
  onClick,
  ...props
}: ButtonLinkProps) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (typeof window !== "undefined" && track) {
      const anchor = event.currentTarget;
      trackCampaignClick(track, anchor.innerText);
    }

    onClick?.(event);
  };

  const linkAttrs = resolveLinkAttrs(href, { rel, target, ugc });

  return (
    <a
      {...props}
      href={href}
      target={linkAttrs.target}
      rel={linkAttrs.rel}
      onClick={track || onClick ? handleClick : undefined}
      className={cn(buttonVariants({ size, variant: look }), className)}
    >
      {children}
    </a>
  );
};

ButtonLink.displayName = "ButtonLink";

type ButtonStyleProps = Pick<
  ButtonProps,
  "size" | "variant" | "track" | "loading" | "disabled"
>;

export type ButtonRouterProps = Omit<
  LinkProps,
  "className" | "activeProps" | "inactiveProps" | "children"
> &
  ButtonStyleProps & {
    className?: string;
    activeClassName?: string;
    inactiveClassName?: string;
    children?: React.ReactNode;
    /** Runs on a click that navigates, before the router takes over. */
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  };

export const ButtonRouter = React.memo(
  React.forwardRef<HTMLAnchorElement, ButtonRouterProps>(
    (
      {
        size,
        variant: look,
        track,
        loading,
        children,
        className,
        activeClassName,
        inactiveClassName,
        to,
        search,
        params,
        hash,
        state,
        mask,
        activeOptions,
        disabled,
        onClick,
      },
      ref,
    ) => {
      const linkRef = useRef<HTMLAnchorElement>(null);
      const combinedRef = useMemo(() => {
        return (node: HTMLAnchorElement | null) => {
          linkRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        };
      }, [ref]);

      const trackClick = useCallback(
        (_e: React.MouseEvent<HTMLAnchorElement>) => {
          if (window && track && linkRef.current) {
            trackCampaignClick(track, linkRef.current.innerText);
          }
        },
        [track],
      );

      const handleClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          trackClick(e);
          onClick?.(e);
        },
        [disabled, trackClick, onClick],
      );

      if (disabled) {
        return (
          <span
            className={cn(
              buttonVariants({ variant: look, size }),
              "pointer-events-none opacity-50",
              className,
            )}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {children}
          </span>
        );
      }

      const baseClassName = cn(
        buttonVariants({ variant: look, size }),
        className,
      );

      return (
        <Link
          ref={combinedRef}
          to={to}
          search={search}
          params={params}
          hash={hash}
          state={state}
          mask={mask}
          activeProps={{
            className: cn(baseClassName, activeClassName),
          }}
          activeOptions={activeOptions}
          inactiveProps={{
            className: cn(baseClassName, inactiveClassName),
          }}
          onClick={handleClick}
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {children}
        </Link>
      );
    },
  ),
);

ButtonRouter.displayName = "ButtonRouter";
